<template>
  <section class="relative overflow-hidden border-b border-default/60 bg-gradient-to-b from-primary/5 via-transparent to-transparent">
    <UContainer class="py-10 sm:py-14 lg:py-16">
      <div class="grid items-center gap-10 lg:grid-cols-2 lg:gap-14">
        <div class="order-2 space-y-5 lg:order-1">
          <UBadge
            v-if="description"
            color="primary"
            variant="subtle"
            size="md"
            class="w-fit"
          >
            {{ description }}
          </UBadge>
          <h1
            v-if="title"
            class="text-3xl font-bold tracking-tight text-highlighted sm:text-4xl lg:text-5xl"
          >
            {{ title }}
          </h1>
          <p
            v-for="(paragraph, index) in introParagraphs"
            :key="index"
            class="text-base leading-relaxed text-muted sm:text-lg"
          >
            {{ paragraph }}
          </p>
          <div class="flex flex-wrap gap-3 pt-2">
            <UButton
              v-if="primaryLabel"
              :to="builderPreview ? undefined : primaryLink"
              size="lg"
              color="primary"
              trailing-icon="i-heroicons-calendar-days"
              :label="primaryLabel"
              @click="builderPreview ? navigate(primaryLink) : undefined"
            />
            <UButton
              v-if="secondaryLabel"
              :to="builderPreview ? undefined : secondaryLink"
              size="lg"
              color="neutral"
              variant="outline"
              :trailing-icon="dir === 'rtl' ? 'i-heroicons-arrow-left' : 'i-heroicons-arrow-right'"
              :label="secondaryLabel"
              @click="builderPreview ? navigate(secondaryLink) : undefined"
            />
            <UButton
              v-if="tertiaryLabel"
              :to="isExternal(tertiaryLink) || isFile(tertiaryLink) ? tertiaryLink : (builderPreview ? undefined : tertiaryLink)"
              :target="isExternal(tertiaryLink) || isFile(tertiaryLink) ? '_blank' : undefined"
              size="lg"
              color="neutral"
              variant="soft"
              trailing-icon="i-heroicons-arrow-down-tray"
              :label="tertiaryLabel"
              @click="builderPreview && !isExternal(tertiaryLink) && !isFile(tertiaryLink) ? navigate(tertiaryLink) : undefined"
            />
          </div>
        </div>

        <div v-if="image" class="order-1 flex justify-center lg:order-2">
          <div class="relative">
            <div class="absolute -inset-4 rounded-3xl bg-primary/10 blur-2xl" aria-hidden="true" />
            <img
              :src="image"
              :alt="imageAlt || title || 'Doctor'"
              class="relative max-h-112 w-full max-w-sm rounded-2xl object-cover shadow-xl ring-1 ring-default/40 sm:max-w-md"
            >
          </div>
        </div>
      </div>
    </UContainer>
  </section>
</template>

<script setup lang="ts">
const dir = useDir()

const props = defineProps<{
  title?: string
  description?: string
  intro?: string
  image?: string
  imageAlt?: string
  primaryLabel?: string
  primaryLink?: string
  secondaryLabel?: string
  secondaryLink?: string
  tertiaryLabel?: string
  tertiaryLink?: string
  builderPreview?: boolean
  onNavigate?: (to: string) => void
}>()

const introParagraphs = computed(() =>
  (props.intro ?? '')
    .split('\n\n')
    .map((p) => p.trim())
    .filter(Boolean),
)

const primaryLink = computed(() => props.primaryLink || '/booking')
const secondaryLink = computed(() => props.secondaryLink || '/services')
const tertiaryLink = computed(() => props.tertiaryLink || '/files/resume.pdf')

function isExternal(to?: string) {
  return Boolean(to && (to.startsWith('http://') || to.startsWith('https://')))
}

function isFile(to?: string) {
  return Boolean(to && (to.startsWith('/files/') || to.endsWith('.pdf')))
}

function navigate(to?: string) {
  if (!to || !props.builderPreview || !props.onNavigate) return
  props.onNavigate(to)
}
</script>
